import React, { useEffect, useState } from 'react';
import { InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField } from '@mui/material';
import { Text, TextSpan } from '../../Atoms/Text';
import {
  getDefaultPreferences,
  getUser,
  getUserPreferencesSubject,
  getUserSubject,
  PrefKey,
  updateUserData,
  updateUserSettings,
  UserPreferences
} from '../../../store/User';
import { SKIP_ACCOUNT_ROLES } from '../../../store/Role';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { useInventory } from '../../../store/Inventory';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';
import { ScheduleSetting, useScheduleSettings } from '../../../store/ScheduleSettings';
import { SchedulePausedChip } from '../../Compounds/CustomTableViews/SchedulePausedChip';
import { fetchScheduleSettings } from '../../../Api/ScheduleSettings/scheduleSettingsApi';

interface HistoricOption {
  name: string;
  value: string;
}

interface EmailNotification {
  title: string;
  switchLabel: string;
  default: boolean;
}

interface InputErrors {
  first_name: string;
  last_name: string;
}

export interface FormMeta {
  email: {
    install_updates_email: EmailNotification;
    support_cases_email: EmailNotification;
    upcoming_expiration_90_email: EmailNotification;
    upcoming_expiration_180_email: EmailNotification;
    status_change_email: EmailNotification;
    comment_created_email: EmailNotification;
    comment_mention_email: EmailNotification;
    order_status_update_email: EmailNotification;
  };
  app: {
    support_cases: EmailNotification;
    upcoming_expiration_90: EmailNotification;
    upcoming_expiration_180: EmailNotification;
    status_change: EmailNotification;
    comment_created: EmailNotification;
    comment_mention: EmailNotification;
    account_merge: EmailNotification;
    order_status_update: EmailNotification;
  };
  historic: {
    disconnects: {
      title: string;
      options: HistoricOption[];
    };
    cancellations: {
      title: string;
      options: HistoricOption[];
    };
  };
}

// A switch the user never saved shows how its setting is delivered, so a `default` has to follow delivery.
const defaultFormMeta: FormMeta = {
  email: {
    install_updates_email: {
      title: 'Service Installations',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification,
    support_cases_email: {
      title: 'Support Cases',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification,
    upcoming_expiration_90_email: {
      title: 'Upcoming Expiration 90 days',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification,
    upcoming_expiration_180_email: {
      title: 'Upcoming Expiration 180 days',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification,
    status_change_email: {
      title: 'Status Changes on Portal Records',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification,
    comment_created_email: {
      title: 'Comment Created',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification,
    comment_mention_email: {
      title: 'Comment Mention',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification,
    order_status_update_email: {
      title: 'Order Status Updates',
      switchLabel: 'Email Notifications',
      default: false
    } as EmailNotification
  },
  app: {
    support_cases: {
      title: 'Support Cases',
      switchLabel: 'App Notifications',
      default: false
    } as EmailNotification,
    upcoming_expiration_90: {
      title: 'Upcoming Expiration 90 days',
      switchLabel: 'App Notifications',
      default: false
    } as EmailNotification,
    upcoming_expiration_180: {
      title: 'Upcoming Expiration 180 days',
      switchLabel: 'App Notifications',
      default: false
    } as EmailNotification,
    status_change: {
      title: 'Status Changes on Portal Records',
      switchLabel: 'App Notifications',
      default: false
    } as EmailNotification,
    comment_mention: {
      title: 'Comments that @Mention me',
      switchLabel: 'App Notifications',
      default: true
    } as EmailNotification,
    comment_created: {
      title: 'All Comments on any item',
      switchLabel: 'App Notifications',
      default: true
    } as EmailNotification,
    account_merge: {
      title: 'Account Merge',
      switchLabel: 'App Notifications',
      default: false
    } as EmailNotification,
    order_status_update: {
      title: 'Order Status Updates',
      switchLabel: 'App Notifications',
      default: false
    } as EmailNotification
  },
  historic: {
    disconnects: {
      title: 'Show disconnects for the last...',
      options: [
        { name: '6 months', value: '6' },
        { name: '12 months', value: '12' },
        { name: '24 months', value: '24' },
        { name: '36 months', value: '36' },
        { name: 'All Time', value: 'All' },
        { name: 'None', value: 'None' }
      ] as HistoricOption[]
    },
    cancellations: {
      title: 'Show cancellations for the last...',
      options: [
        { name: 'All Time', value: 'All' },
        { name: 'None', value: 'None' }
      ] as HistoricOption[]
    }
  }
};

const switchSx = {
  '.Mui-checked': {
    color: '#3B6FE8 !important'
  },
  '.MuiSwitch-track': {
    backgroundColor: '#878EBE !important'
  }
};

const MySettingsTab = () => {
  const [settings, setSettings] = useState<UserPreferences>(getDefaultPreferences());
  const [savingSettings, setSavingSettings] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [inputErrors, setInputErrors] = useState<InputErrors>({ first_name: '', last_name: '' });
  const [sharedViews, setSharedViews] = useState<ScheduleSetting[]>([]);
  const { fetchInventory } = useInventory();
  const { optOut, optIn } = useScheduleSettings();
  const { setSnack } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    const settingsSub = getUserPreferencesSubject().subscribe((preferences) => setSettings(preferences));
    const userSubscription = getUserSubject().subscribe((user) => {
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setEmail(user.email);
    });

    const loadSharedViews = async () => {
      const { data, error } = await fetchScheduleSettings();
      if (error) {
        setSnack({ type: 'error', message: 'Failed to load shared views.', open: true });
        return;
      }
      const currentUser = getUser();
      const currentUserId = Number(currentUser.id);
      const shared = (data ?? []).filter((s: ScheduleSetting) => s.recipient_portal_user_ids.includes(currentUserId));
      setSharedViews(shared);
    };

    loadSharedViews();

    return () => {
      if (userSubscription) userSubscription.unsubscribe();
      if (settingsSub) settingsSub.unsubscribe();
    };
  }, []);

  const toggleSharedViewEmail = async (setting: ScheduleSetting) => {
    const isSubscribed = setting.recipients.includes(email);
    const data = isSubscribed ? await optOut(setting.id) : await optIn(setting.id);
    if (data) {
      setSharedViews((prev) => prev.map((s) => (s.id === setting.id ? data : s)));
    }
  };

  const handleSave = async () => {
    setSavingSettings(true);
    const { error } = await updateUserSettings(settings, 'User Setting');
    const { error: errorUser } = await updateUserData(firstName, lastName);
    if (errorUser) setInputErrors({ ...inputErrors });
    setSavingSettings(false);
    if (error || errorUser) {
      setSnack({
        type: 'error',
        message: 'Failed to save settings.',
        open: true
      });
    } else {
      fetchInventory(true);
      setSnack({
        type: 'success',
        message: 'Settings saved.',
        open: true
      });
    }
  };

  const handleSettingChange = (id: string, section: PrefKey, event?: SelectChangeEvent) => {
    const settingsUpdate = { ...settings };

    if (section === 'notifications') {
      const updatedSection = settingsUpdate?.content?.notifications;
      if (!updatedSection) return;
      let updated = updatedSection.find((s) => s.id === id);
      if (!updated) {
        const defaultItem = id.includes('email')
          ? defaultFormMeta.email[id as keyof typeof defaultFormMeta.email]
          : defaultFormMeta.app[id as keyof typeof defaultFormMeta.app];

        updated = { id, index: updatedSection.length, enabled: !defaultItem.default };
        updatedSection.push(updated);
      } else {
        updated.enabled = !updated.enabled;
      }
    } else {
      const updatedSection = settingsUpdate?.content?.historic;
      if (!updatedSection) return;
      const updated = updatedSection.find((s) => s.id === id);
      if (!updated) return;
      updated.value = event?.target?.value;
    }

    setSettings(settingsUpdate);
  };

  const renderNotifications = () => {
    const emailKeys: string[] = Object.keys(defaultFormMeta.email);
    return Object.keys(defaultFormMeta.app).map((notif) => {
      const appNotification = settings?.content.notifications.find((i) => i.id === notif);
      const defaultApp = defaultFormMeta.app[notif as keyof typeof defaultFormMeta.app];
      const emailNotificaiton = settings?.content.notifications.find((i) => i.id === `${notif}_email`);
      const defaultEmail = defaultFormMeta.email[`${notif}_email` as keyof typeof defaultFormMeta.email];

      return (
        <tr key={`${notif}-setting`}>
          <td className="pl-2">
            <Text
              color="grey6"
              weight="normal"
              size="sm">
              {defaultFormMeta.app[notif as keyof typeof defaultFormMeta.app].title}
            </Text>
          </td>
          <td>
            <div className="flex !ml-auto items-center space-x-3">
              <div>
                <Switch
                  sx={switchSx}
                  size="small"
                  checked={appNotification ? appNotification.enabled : defaultApp.default}
                  onChange={() => handleSettingChange(notif, 'notifications')}
                />
              </div>
            </div>
          </td>
          <td>
            <div className="flex !ml-auto items-center space-x-3">
              {emailKeys.includes(`${notif}_email`) && (
                <div>
                  <Switch
                    sx={switchSx}
                    size="small"
                    checked={emailNotificaiton ? emailNotificaiton.enabled : defaultEmail?.default}
                    onChange={() => handleSettingChange(`${notif}_email`, 'notifications')}
                  />
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFirstName(value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLastName(value);
  };

  const validate = (type: string, value: string) => {
    const errorsUpdate = { ...inputErrors, [type]: '' };
    switch (type) {
      case 'last_name':
      case 'first_name':
        if (!validator.isAlpha(value.replace(/\s|-/g, ''))) errorsUpdate[type] = 'Enter a valid name.';
        break;
    }
    setInputErrors(errorsUpdate);
  };

  return (
    <div>
      <div className="flex mt-4 px-1">
        <Text
          className="font-semibold w-full"
          color="grey9"
          size="sm">
          Personal Details
        </Text>
      </div>
      <hr className="my-2 h-0.5 border-t-1" />
      <div className="pl-2">
        <div className="flex flex-row pb-4">
          <InputLabel
            className="basis-1/5 mt-2"
            htmlFor="first_name"
            sx={{ fontSize: '.8rem', color: '#606060' }}>
            First Name
          </InputLabel>
          <TextField
            type="text"
            className="w-full flex-initial"
            name="first_name"
            size={'small'}
            value={firstName || ''}
            placeholder="First Name"
            required={true}
            onBlur={() => validate('first_name', firstName || '')}
            onChange={handleFirstNameChange}
            error={!!inputErrors.first_name}
            helperText={inputErrors.first_name}
            InputProps={{ style: { fontSize: '.8rem', height: '2rem' } }}
          />
        </div>
        <div className="flex flex-row pb-4">
          <InputLabel
            className="basis-1/5 mt-2"
            htmlFor="last_name"
            sx={{ fontSize: '.8rem', color: '#606060' }}>
            Last Name
          </InputLabel>
          <TextField
            type="text"
            className="w-full"
            name="last_name"
            size={'small'}
            value={lastName || ''}
            placeholder="Last Name"
            required={true}
            onBlur={() => validate('last_name', lastName || '')}
            onChange={handleLastNameChange}
            error={!!inputErrors.last_name}
            helperText={inputErrors.last_name}
            InputProps={{ style: { fontSize: '.8rem', height: '2rem' } }}
          />
        </div>
        {getUser().roles.every(({ name }) => name && !SKIP_ACCOUNT_ROLES.includes(name)) && (
          <div className="flex flex-row pb-4">
            <InputLabel
              className="basis-1/5"
              htmlFor="email"
              sx={{ fontSize: '.8rem', color: '#606060' }}>
              Email
            </InputLabel>
            <TextSpan
              className="w-full"
              size="sm">
              {email}
            </TextSpan>
          </div>
        )}
      </div>
      <table className="mt-4 w-full [&_tr]:border-y">
        <thead>
          <tr className="border-none text-left [&>*]:font-normal [&>*]:p-1">
            <th>
              <Text
                className="font-semibold w-full"
                color="grey9"
                size="sm">
                Notifications
              </Text>
            </th>
            <th>
              <Text
                color="grey9"
                weight="medium"
                size="sm">
                In-App
              </Text>
            </th>
            <th>
              <Text
                color="grey9"
                weight="medium"
                size="sm">
                Email
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {renderNotifications()}
          <tr>
            <td className="pl-2 py-1">
              <Text
                className="font-semibold"
                color="grey9"
                size="sm">
                Shared Views
              </Text>
            </td>
            <td />
            <td />
          </tr>
          {sharedViews.map((setting) => (
            <tr key={setting.id}>
              <td className="pl-2">
                <div className="flex items-center gap-2">
                  <Text
                    color="grey6"
                    weight="normal"
                    size="sm">
                    {setting.name}
                  </Text>
                  {/* Beside the name, not in the In-App column: a pause is not a notification channel. */}
                  {setting.paused_at && <SchedulePausedChip reason={setting.paused_reason} />}
                </div>
              </td>
              <td />
              <td>
                <div className="flex !ml-auto items-center space-x-3">
                  <div>
                    <Switch
                      sx={switchSx}
                      size="small"
                      checked={setting.recipients.includes(email)}
                      onChange={() => toggleSharedViewEmail(setting)}
                    />
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex mt-8 px-1">
        <Text
          className="font-semibold w-full"
          color="grey9"
          size="sm">
          Historic Data
        </Text>
      </div>
      <hr className="my-2 h-0.5 border-t-1" />
      {settings?.content.historic.map((item) => (
        <div
          key={item.id}
          className="flex items-center space-x-4 px-1 pb-2">
          <Text
            color="grey6"
            size="sm">
            {defaultFormMeta.historic[item.id as keyof typeof defaultFormMeta.historic].title}
          </Text>
          <div className="flex !ml-auto items-center space-x-3">
            <div>
              <Select
                onChange={(event) => handleSettingChange(item.id, 'historic', event)}
                sx={{ fontSize: '.875rem', height: '1.5rem' }}
                value={item.value}>
                {defaultFormMeta.historic[item.id as keyof typeof defaultFormMeta.historic].options.map((option) => (
                  <MenuItem
                    key={option.name}
                    value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      ))}

      <hr className="mt-3 h-0.5 border-t-2" />

      <div className="justify-between mt-5">
        <button
          onClick={() => navigate('/')}
          className={DEFAULT_CANCEL_BUTTON_STYLE}>
          Back
        </button>
        <button
          disabled={savingSettings || !!inputErrors.first_name || !!inputErrors.last_name}
          onClick={handleSave}
          className={`${DEFAULT_ADVANCE_BUTTON_STYLE} float-right`}>
          Save
        </button>
      </div>
    </div>
  );
};

export default MySettingsTab;
